import { OnboardingState } from '@wix/ai-scheduling-assistant-common-lib/entities';

export type OnboardingRoute = 'kickoff' | 'resume' | 'none';

/**
 * Where a signed-in caller with a resolved recipient belongs: the assistant's opening greeting
 * (`kickoff`), their in-progress onboarding thread (`resume`), or the normal home (`none`).
 *
 * A DERIVED decision, not a URL flag. This is the web mirror of native's single onboarding classifier —
 * iOS `SymphonyStore.onboardingRouteDecision` (`ios/Symphony/Store/SymphonyStore.swift`) and Android
 * `SymphonyStore.onboardingRouteDecision` (`android/.../SymphonyStore.kt`) — kept as a 1:1 port so the
 * three surfaces agree on when a user is greeted. It replaced web's `?welcome=true` param + the
 * once-per-session autostart flag: a signal every navigation path had to remember to attach, and one that
 * — when a path forgot it — stranded a created-but-un-onboarded user on the bare home (PR #7717).
 *
 * Inputs, all pure:
 *  - `onboardingState` — the recipient's server-authoritative state (the primary signal).
 *  - `finishedOnboarding` — the legacy boolean; the fallback/tie-breaker for GREETED/ENGAGED and for the
 *    UNKNOWN/absent state.
 *  - `hadStoredConversation` — whether the tenant already has any conversation. The idempotency signal:
 *    once the greeting has created a thread this is true, so a fresh kickoff can't recur.
 *  - `kickoffAlreadyFired` — an in-session once-guard the caller holds, so the decision short-circuits to
 *    `none` after the greeting has been kicked off this mount even before its conversation lands (mirrors
 *    native `onboardingKickoffFired`).
 *
 * A `resume` for a caller who turns out to have no conversation to resume falls back to a fresh `kickoff`
 * at the CALL site (the client hook / native `enterOnboardingResume`), not here — this stays pure.
 */
export function onboardingRouteDecision(input: {
  onboardingState?: OnboardingState;
  finishedOnboarding?: boolean;
  hadStoredConversation: boolean;
  kickoffAlreadyFired: boolean;
}): OnboardingRoute {
  if (input.kickoffAlreadyFired) return 'none';
  switch (input.onboardingState) {
    case OnboardingState.ONBOARDING_COMPLETED:
      return 'none';
    case OnboardingState.ONBOARDING_GREETED:
    case OnboardingState.ONBOARDING_ENGAGED:
      // A stale mid-flight state on a finished recipient is completion residue — never bounce them back in.
      return input.finishedOnboarding === true ? 'none' : 'resume';
    case OnboardingState.ONBOARDING_NOT_STARTED:
      return 'kickoff';
    default:
      // UNKNOWN_ONBOARDING_STATE or absent — legacy recipients, and freshly-created ones the server stamps
      // UNKNOWN (the orion-pack-creator case): resume a never-finished caller (the call site falls back to
      // kickoff when there is nothing to resume); otherwise leave them on the home.
      return input.finishedOnboarding === false && !input.hadStoredConversation ? 'resume' : 'none';
  }
}
